/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { useFeature } from 'flagged';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  Text,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';
import styled from 'styled-components';

/**
 * Internal dependencies
 */
import { Color, Row } from '../../../form';
import Tooltip from '../../../tooltip';
import { useUploader } from '../../../../app/uploader';
import useRichTextFormatting from './useRichTextFormatting';
import TextureTransformControls from './textureTransformControls';
import { hasTextTextureDimensions } from '../../../../elements/text/utils/getTextTextureTransform';

const TEXT_TEXTURE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';
const TEXT_TEXTURE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];
const TEXT_TEXTURE_UPLOAD_SOURCE = 'text-texture';

const TextureControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TexturePreview = styled.div`
  width: 32px;
  height: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.defaultNormal};
  border-radius: 4px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const TextureError = styled(Text)`
  flex-basis: 100%;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.fg.negative};
`;

function ColorControls({ selectedElements, pushUpdate, textColorRef }) {
  const isTextTextureEnabled = useFeature('useStudioTextTextures');
  const textureInputRef = useRef(null);
  const [isUploadingTexture, setIsUploadingTexture] = useState(false);
  const [textureUploadError, setTextureUploadError] = useState(null);
  const {
    actions: { uploadFile },
  } = useUploader();
  const {
    textInfo: { color },
    handlers: { handleSetColor },
  } = useRichTextFormatting(selectedElements, pushUpdate);
  const textTexture = selectedElements.find(
    ({ textTexture: texture }) => texture?.src
  )?.textTexture;
  const hasTextTexture = selectedElements.some(
    ({ textTexture }) => textTexture?.src
  );
  let uploadTextureButtonText = __('Upload texture', 'web-stories');
  if (hasTextTexture) {
    uploadTextureButtonText = __('Replace texture', 'web-stories');
  }
  if (isUploadingTexture) {
    uploadTextureButtonText = __('Uploading...', 'web-stories');
  }
  const removeTextureButtonLabel = __('Remove texture', 'web-stories');

  const uploadTexture = useCallback(
    async (file) => {
      if (!file) {
        return;
      }

      if (!TEXT_TEXTURE_MIME_TYPES.includes(file.type)) {
        setTextureUploadError(
          __('Please choose a PNG, JPG, or WebP image.', 'web-stories')
        );
        return;
      }

      setIsUploadingTexture(true);
      setTextureUploadError(null);

      try {
        const uploadedResource = await uploadFile(
          `text-texture-${Date.now()}`,
          file,
          { media_source: TEXT_TEXTURE_UPLOAD_SOURCE }
        );

        if (!uploadedResource?.src) {
          throw new Error(
            __('Texture upload did not return a URL.', 'web-stories')
          );
        }

        pushUpdate(
          ({ textAlign }) => ({
            textTexture: {
              src: uploadedResource.src,
              sizing: 'cover',
              ...(hasTextTextureDimensions(uploadedResource)
                ? {
                    width: uploadedResource.width,
                    height: uploadedResource.height,
                  }
                : {}),
              scale: 1,
              position: { x: 0.5, y: 0.5 },
            },
            ...(textAlign === 'justify' ? { textAlign: 'left' } : {}),
          }),
          true
        );
      } catch (err) {
        setTextureUploadError(
          err?.message ||
            __('Texture upload failed. Please try again.', 'web-stories')
        );
      } finally {
        setIsUploadingTexture(false);
      }
    },
    [pushUpdate, uploadFile]
  );

  const handleTextureInputChange = useCallback(
    (evt) => {
      const file = evt.target.files?.[0];
      evt.target.value = '';
      uploadTexture(file);
    },
    [uploadTexture]
  );

  const openTextureUpload = useCallback(() => {
    textureInputRef.current?.click();
  }, []);

  const removeTexture = () => {
    setTextureUploadError(null);
    pushUpdate({ textTexture: null }, true);
  };

  return (
    <>
      <Row>
        <Color
          data-testid="text.color"
          allowsGradient
          value={color}
          onChange={handleSetColor}
          label={__('Text color', 'web-stories')}
          labelId="text-color-label"
          changedStyle="color"
          ref={textColorRef}
        />
      </Row>
      {isTextTextureEnabled && (
        <Row>
          <TextureControls>
            <input
              hidden
              ref={textureInputRef}
              type="file"
              accept={TEXT_TEXTURE_ACCEPT}
              data-testid="text.textureUploadInput"
              onChange={handleTextureInputChange}
            />
            {hasTextTexture && (
              <TexturePreview
                aria-label={__('Current text texture', 'web-stories')}
                data-testid="text.texturePreview"
                role="img"
                style={{
                  backgroundImage: `url("${textTexture.src.replace(
                    /"/g,
                    '\\"'
                  )}")`,
                }}
              />
            )}
            <Button
              data-testid="text.uploadTexture"
              disabled={isUploadingTexture}
              size={BUTTON_SIZES.SMALL}
              type={BUTTON_TYPES.SECONDARY}
              onClick={openTextureUpload}
            >
              {uploadTextureButtonText}
            </Button>
            <Tooltip hasTail title={removeTextureButtonLabel}>
              <Button
                aria-label={removeTextureButtonLabel}
                data-testid="text.removeTexture"
                disabled={!hasTextTexture || isUploadingTexture}
                size={BUTTON_SIZES.SMALL}
                type={BUTTON_TYPES.TERTIARY}
                variant={BUTTON_VARIANTS.SQUARE}
                onClick={removeTexture}
              >
                <Icons.Trash />
              </Button>
            </Tooltip>
            {hasTextTexture && (
              <TextureTransformControls
                key={selectedElements
                  .map(
                    ({ id, textTexture }) => `${id}:${textTexture?.src || ''}`
                  )
                  .join('|')}
                selectedElements={selectedElements}
                pushUpdate={pushUpdate}
                disabled={isUploadingTexture}
              />
            )}
            {textureUploadError && (
              <TextureError
                data-testid="text.textureUploadError"
                role="alert"
                size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
              >
                {textureUploadError}
              </TextureError>
            )}
          </TextureControls>
        </Row>
      )}
    </>
  );
}

ColorControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
  textColorRef: PropTypes.func,
};

export default ColorControls;
